/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_REPAIR_METHOD } from 'app/AppRouter';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { fetchIAMAvailableModulesRequestSaga, getIAMAvailableModules } from 'domains/catalog/CatalogIam.store';
import { UserRole } from 'domains/user';
import { NotAuthorizedLabel, notAvailableTab, notLoadedTab } from 'pages/CatalogPage/common/CatalogBanner/TabCommon';
import { RepairMethod } from 'pages/CatalogPage/IAM/RepairMethod/RepairMethod';
import { theme } from 'styles';
import { LinkTabItem, Text } from 'UI';
import { getCondArrayItem, getData, hasUserAnyRight, hasUserR3WithSubscriptionL234, NOT_FOUND } from 'utils';

const hasAccessToRepairMethods = ({
  isIam,
  userRights,
  versionCode,
}: {
  isIam: boolean;
  userRights: UserRole[] | undefined;
  versionCode: string | undefined;
}): boolean => {
  if (isIam) {
    return hasUserAnyRight(
      [UserRole.IAM_FAST_ACCESS, UserRole.IAM_PRO_ACCESS, UserRole.IAM_PRO_PLUS_ACCESS],
      userRights,
    );
  }
  return Boolean(versionCode) && hasUserR3WithSubscriptionL234(userRights);
};

export const TabRepairMethods = ({
  isIam,
  versionCode,
  userRights,
  renderVehicleTabContent,
  vehicleDetail,
  includeVehicleTabContent = true,
}: {
  isIam: boolean;
  versionCode: string | undefined;
  userRights: UserRole[] | undefined;
  renderVehicleTabContent: JSX.Element;
  vehicleDetail: VehicleDetail;
  includeVehicleTabContent?: boolean;
}): LinkTabItem[] => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const label = t('catalog.parts.banner.repair_method.title', 'Repair Method');
  const availableDataModule = useSelector(getIAMAvailableModules);
  const availableDataModuleData = getData(availableDataModule);
  const getDataAvailableManuals = () =>
    availableDataModuleData?.availableData?.find((d) => d.name === 'manuals')?.dataAvailable;
  const key = 10;
  const link = `${getCatalogSourceUrl(vehicleDetail.catalogSource)}/${vehicleDetail.vehicleKey}${ROUTER_REPAIR_METHOD}`;
  const handleTab = () => {
    if (availableDataModule === NOT_FOUND) {
      return notAvailableTab({ key, label, dataCy: 'tab-repair-method-not-available', link });
    }
    if (!availableDataModule && hasUserR3WithSubscriptionL234(userRights)) {
      //R3 or R3_MOTRIO with enough rights must trigger loading by clicking on tab
      dispatch(fetchIAMAvailableModulesRequestSaga());
      return notLoadedTab({ key, label, link, dataCy: 'tab-repair-method-not-loaded' });
    }
    return (
      getDataAvailableManuals() &&
      // for user with IAM rights
      (hasAccessToRepairMethods({ isIam, userRights, versionCode })
        ? {
            key,
            label: (
              <Text type={'lead_text_white'} noWrap>
                {label}
              </Text>
            ),
            component: (
              <>
                {includeVehicleTabContent && renderVehicleTabContent}
                <RepairMethod />
              </>
            ),
            link,
            dataCy: 'tab-repair-method',
          }
        : // for IAM car without user rights
          {
            key,
            label: <NotAuthorizedLabel label={label} />,
            component: <></>,
            link: '',
            dataCy: 'tab-repair-method-no-access',
            staticWithColor: theme.color.information_active,
          })
    );
  };
  return getCondArrayItem(handleTab());
};
